/**
 * UserLabel — single source of truth for rendering a user identity across
 * every dashboard surface that now has a Name column/field (v1.4 SSOT rollout
 * after the backend added `realName` via LEFT JOIN on `slack_user_directory`).
 *
 * Contract:
 *   - Primary line: `realName` (em-dash when null/empty).
 *   - Secondary line: `@username` in muted type.
 *   - `variant`:
 *       * `'stacked'` (default) — two-line block for headers, cards, wide cells.
 *       * `'cell'` — compact table cell where the Name is the only visible
 *         token. The Username column renders itself separately; passing
 *         `variant='cell'` suppresses the secondary `@username` line so we
 *         don't duplicate it in the adjacent column.
 *       * `'inline'` — single-line layout joining Name and `@username` with
 *         a middot separator (useful for row meta strips like ErrorBucketModal).
 *
 * `formatUserLabel(realName, username)` returns the canonical
 * "Real Name (@handle)" / "@handle" string used by UserMultiSelect-style
 * dropdowns — keep it as a plain function so callers don't pull React into
 * their list-item code.
 */

import * as React from 'react';

import { cn } from '@/lib/utils';

export type UserLabelVariant = 'stacked' | 'cell' | 'inline';

export interface UserLabelProps {
  /** `slack_user_directory.real_name`; null or empty renders as em-dash. */
  name: string | null | undefined;
  /** Slack username / handle — always shown verbatim (sans @) as the key. */
  username: string | null | undefined;
  variant?: UserLabelVariant;
  /** Optional extra class merged onto the outer wrapper. */
  className?: string;
  /** Optional testid passthrough; defaults to `user-label`. */
  testId?: string;
}

const EM_DASH = '—';

function normalizedName(name: string | null | undefined): string {
  if (!name) return EM_DASH;
  const trimmed = name.trim();
  return trimmed.length > 0 ? trimmed : EM_DASH;
}

/**
 * Canonical "Real Name (@handle)" formatter — null/empty realName collapses
 * to `@handle` alone (matches UserMultiSelect's existing "just username" row
 * for users without a directory entry). Safe for use in dropdown menus,
 * chips, aria labels.
 */
export function formatUserLabel(
  realName: string | null | undefined,
  username: string | null | undefined,
): string {
  const uname = (username ?? '').trim();
  const rname = (realName ?? '').trim();
  if (!uname) return rname || EM_DASH;
  if (!rname) return `@${uname}`;
  return `${rname} (@${uname})`;
}

export function UserLabel({
  name,
  username,
  variant = 'stacked',
  className,
  testId = 'user-label',
}: UserLabelProps): React.ReactElement {
  const displayName = normalizedName(name);
  const handle = (username ?? '').trim();

  if (variant === 'cell') {
    // Name-only cell — Username column renders the handle separately. Keep
    // the markup a single span so sticky-first-column / tabular alignment
    // stays predictable.
    return (
      <span
        data-testid={testId}
        data-variant="cell"
        title={handle ? `@${handle}` : undefined}
        className={cn('truncate inline-block max-w-[14rem] align-middle', className)}
      >
        {displayName}
      </span>
    );
  }

  if (variant === 'inline') {
    return (
      <span
        data-testid={testId}
        data-variant="inline"
        className={cn('inline-flex items-baseline gap-1.5', className)}
      >
        <span className="font-medium text-foreground">{displayName}</span>
        {handle ? (
          <span className="text-muted-foreground">@{handle}</span>
        ) : null}
      </span>
    );
  }

  // stacked (default) — two lines for headers/cards.
  return (
    <span
      data-testid={testId}
      data-variant="stacked"
      className={cn('flex flex-col min-w-0 leading-tight', className)}
    >
      <span className="font-medium text-foreground truncate">{displayName}</span>
      {handle ? (
        <span className="text-xs text-muted-foreground truncate">@{handle}</span>
      ) : null}
    </span>
  );
}
